import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { X, Download, Eye, RotateCcw, Info, FileText, Clock } from 'lucide-react';
import axios from '../../config/axios';
import Navbar from './Navbar';
import Footer from './Footer';
import Button from '../common/Button';

const PublicViewReturn = () => {
  const { returnId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [returnData, setReturnData] = useState(null);
  const [receiptPreview, setReceiptPreview] = useState(null);
  const [downloadingReceipt, setDownloadingReceipt] = useState(false);
  const [loadingReceipt, setLoadingReceipt] = useState(false);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchReturn();
  }, [returnId]);

  const fetchReturn = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await axios.get(`/shipments/returned/${returnId}`);
      const returnInfo = response.data.data;

      if (returnInfo) {
        setReturnData(returnInfo);
        if (returnInfo.has_payment_receipt) {
          fetchReceiptPreview();
        }
      } else {
        setError('Return request not found');
      }
    } catch (error) {
      console.error('Error fetching return:', error);
      setError(error.response?.data?.error || 'Failed to load return request');
    } finally {
      setLoading(false);
    }
  };

  const fetchReceiptPreview = async () => {
    try {
      setLoadingReceipt(true);
      const response = await axios.get(`/shipments/returned/${returnId}/receipt`, {
        responseType: 'blob'
      });

      const reader = new FileReader();
      reader.onload = () => {
        const base64String = reader.result.split(',')[1];
        const blob = response.data;
        const mimeType = blob.type || 'application/octet-stream';

        setReceiptPreview({
          receipt_base64: base64String,
          mime_type: mimeType,
          filename: `payment_receipt_${returnId}`,
        });
      };
      reader.readAsDataURL(response.data);
    } catch (error) {
      console.error('Error loading receipt preview:', error);
    } finally {
      setLoadingReceipt(false);
    }
  };

  const handleDownloadReceipt = async () => {
    try {
      setDownloadingReceipt(true);
      const response = await axios.get(`/shipments/returned/${returnId}/receipt`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', receiptPreview?.filename || 'payment_receipt.pdf');
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading receipt:', error);
      alert('Failed to download payment receipt');
    } finally {
      setDownloadingReceipt(false);
    }
  };

  const getStatusBadgeColor = (status) => {
    const colors = {
      open: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      close: 'bg-green-100 text-green-800 border-green-200',
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      approved: 'bg-green-100 text-green-800 border-green-200',
      rejected: 'bg-red-100 text-red-800 border-red-200',
    };
    return colors[status?.toLowerCase()] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const isPaymentInitiated = returnData?.payment_initiated?.approved || false;
  const isPaymentRefunded = returnData?.payment_refunded?.approved || false;

  const ReceiptModal = () => {
    if (!showReceiptModal) return null;

    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-card shadow-strong max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
          <div className="bg-brand px-6 py-4 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-white">Payment Receipt</h3>
            <button onClick={() => setShowReceiptModal(false)} className="text-white hover:bg-white/20 p-2 rounded-btn transition" aria-label="Close">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="flex-1 overflow-auto p-6">
            {loadingReceipt ? (
              <div className="flex items-center justify-center h-96">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brand mx-auto mb-4" />
                  <p className="text-secondary">Loading receipt...</p>
                </div>
              </div>
            ) : receiptPreview ? (
              <div className="flex justify-center">
                <img
                  src={`data:${receiptPreview.mime_type};base64,${receiptPreview.receipt_base64}`}
                  alt="Payment Receipt"
                  className="max-w-full h-auto rounded-card shadow-card border border-gray-200"
                />
              </div>
            ) : (
              <div className="text-center text-secondary">No receipt available</div>
            )}
          </div>

          <div className="bg-gray-50 border-t border-gray-100 px-6 py-4 flex gap-3">
            <Button variant="outline" className="flex-1" onClick={() => setShowReceiptModal(false)}>
              Close
            </Button>
            <Button variant="success" className="flex-1" icon={Download} onClick={handleDownloadReceipt} loading={downloadingReceipt}>
              {downloadingReceipt ? 'Downloading...' : 'Download'}
            </Button>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 font-sans flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center pt-24">
          <div className="text-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brand mx-auto mb-4" />
            <p className="text-secondary font-medium">Loading return request...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !returnData) {
    return (
      <div className="min-h-screen bg-gray-50 font-sans">
        <Navbar />
        <div className="container mx-auto px-4 lg:px-6 pt-28 pb-16 flex items-center justify-center min-h-[60vh]">
          <div className="bg-white rounded-card shadow-card border border-red-100 p-8 max-w-md text-center">
            <p className="text-danger text-lg font-semibold mb-2">Return Request Not Found</p>
            <p className="text-secondary mb-6 text-sm">{error || 'The return request you are looking for does not exist.'}</p>
            <Button variant="primary" onClick={() => navigate('/public-track-shipment')}>
              Track Shipment
            </Button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 font-sans">
      <Navbar />

      <section className="bg-gradient-to-r from-brand to-brand-hover text-white pt-28 pb-10 md:pt-32 md:pb-12">
        <div className="container mx-auto px-4 lg:px-6">
          <div className="flex items-center gap-3 mb-2">
            <RotateCcw className="w-6 h-6" />
            <h1 className="text-2xl md:text-3xl font-semibold">Return Request Details</h1>
          </div>
          <p className="text-blue-100">
            Return ID: <span className="font-semibold text-white">#{returnData.return_id}</span>
          </p>
        </div>
      </section>

      <div className="container mx-auto px-4 lg:px-6 py-8 max-w-4xl space-y-6">
        {/* Status Banner */}
        <div className={`p-4 rounded-card border ${getStatusBadgeColor(returnData.return_status)}`}>
          <p className="font-semibold text-lg uppercase">{returnData.return_status}</p>
          <p className="text-xs mt-1 opacity-80">
            {returnData.return_status?.toLowerCase() === 'open' && 'Awaiting payment processing'}
            {returnData.return_status?.toLowerCase() === 'close' && 'Return request completed'}
            {returnData.return_status?.toLowerCase() === 'pending' && 'Return request pending approval'}
          </p>
        </div>

        {/* Return Information */}
        <div className="bg-white rounded-card shadow-card border border-gray-100 overflow-hidden">
          <div className="bg-brand px-6 py-4">
            <h3 className="text-base font-semibold text-white flex items-center gap-2">
              <Info className="w-5 h-5" />
              Return Information
            </h3>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="bg-brand-light rounded-card p-4 border border-blue-100">
                <p className="text-xs font-medium text-secondary uppercase mb-1">Return ID</p>
                <p className="font-semibold text-brand font-mono">#{returnData.return_id}</p>
              </div>
              <div className="bg-brand-light rounded-card p-4 border border-blue-100">
                <p className="text-xs font-medium text-secondary uppercase mb-1">Shipment ID</p>
                <p className="font-semibold text-brand font-mono">#{returnData.shipment_id}</p>
              </div>
              <div className="bg-gray-50 rounded-card p-4 border border-gray-100">
                <p className="text-xs font-medium text-secondary uppercase mb-1">Created By</p>
                <p className="text-dark font-medium">{returnData.created_by || 'N/A'}</p>
              </div>
              <div className="bg-gray-50 rounded-card p-4 border border-gray-100">
                <p className="text-xs font-medium text-secondary uppercase mb-1">Created Date</p>
                <p className="text-dark">
                  {returnData.created_at
                    ? new Date(returnData.created_at).toLocaleString('en-IN', {
                        day: '2-digit', month: 'short', year: 'numeric',
                        hour: '2-digit', minute: '2-digit',
                      })
                    : 'N/A'}
                </p>
              </div>
              <div className="bg-gray-50 rounded-card p-4 border border-gray-100">
                <p className="text-xs font-medium text-secondary uppercase mb-1">Return Status</p>
                <span className={`inline-block px-3 py-1 rounded-btn text-xs font-semibold uppercase ${getStatusBadgeColor(returnData.return_status)}`}>
                  {returnData.return_status}
                </span>
              </div>
              <div className="bg-gray-50 rounded-card p-4 border border-gray-100">
                <p className="text-xs font-medium text-secondary uppercase mb-1">Last Updated</p>
                <p className="text-dark">
                  {returnData.updated_at
                    ? new Date(returnData.updated_at).toLocaleString('en-IN', {
                        day: '2-digit', month: 'short', year: 'numeric',
                        hour: '2-digit', minute: '2-digit',
                      })
                    : 'N/A'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Reason for Return */}
        <div className="bg-white rounded-card shadow-card border border-gray-100 overflow-hidden">
          <div className="bg-brand px-6 py-4">
            <h3 className="text-base font-semibold text-white flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Reason for Return
            </h3>
          </div>
          <div className="p-6">
            <div className="bg-brand-light border border-blue-100 p-4 rounded-card">
              <p className="text-dark whitespace-pre-wrap text-sm">{returnData.reason_for_return}</p>
            </div>
          </div>
        </div>

        {/* Payment Timeline */}
        <div className="bg-white rounded-card shadow-card border border-gray-100 overflow-hidden">
          <div className="bg-brand px-6 py-4 flex flex-wrap items-center justify-between gap-3">
            <h3 className="text-base font-semibold text-white flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Payment Timeline
            </h3>
            {returnData.has_payment_receipt && (
              <Button variant="secondary" size="sm" icon={Eye} onClick={() => setShowReceiptModal(true)}>
                View Receipt
              </Button>
            )}
          </div>
          <div className="p-6 space-y-6">
            <div className="flex gap-4">
              <div className="flex flex-col items-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold text-white ${isPaymentInitiated ? 'bg-success' : 'bg-gray-300'}`}>1</div>
                <div className={`w-0.5 h-12 my-1 ${isPaymentRefunded ? 'bg-success' : 'bg-gray-200'}`} />
              </div>
              <div className="flex-1">
                <p className="font-semibold text-dark">Payment Initiated</p>
                {isPaymentInitiated ? (
                  <div className="text-sm text-secondary mt-2 bg-green-50 p-3 rounded-card border border-green-100">
                    <p className="mb-1"><span className="font-medium">By:</span> {returnData.payment_initiated?.initiated_by?.user_name}</p>
                    <p><span className="font-medium">Date:</span>{' '}
                      {returnData.payment_initiated?.timestamp
                        ? new Date(returnData.payment_initiated.timestamp).toLocaleString('en-IN', {
                            day: '2-digit', month: 'short', year: 'numeric',
                            hour: '2-digit', minute: '2-digit',
                          })
                        : 'N/A'}
                    </p>
                  </div>
                ) : (
                  <p className="text-sm text-secondary mt-2 bg-gray-50 p-3 rounded-card border border-gray-100">
                    Pending - Payment not yet initiated
                  </p>
                )}
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex flex-col items-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold text-white ${isPaymentRefunded ? 'bg-success' : 'bg-gray-300'}`}>2</div>
              </div>
              <div className="flex-1">
                <p className="font-semibold text-dark">Payment Refunded</p>
                {isPaymentRefunded ? (
                  <div className="text-sm text-secondary mt-2 bg-green-50 p-3 rounded-card border border-green-100">
                    <p className="mb-1"><span className="font-medium">By:</span> {returnData.payment_refunded?.initiated_by?.user_name}</p>
                    <p><span className="font-medium">Date:</span>{' '}
                      {returnData.payment_refunded?.timestamp
                        ? new Date(returnData.payment_refunded.timestamp).toLocaleString('en-IN', {
                            day: '2-digit', month: 'short', year: 'numeric',
                            hour: '2-digit', minute: '2-digit',
                          })
                        : 'N/A'}
                    </p>
                  </div>
                ) : (
                  <p className="text-sm text-secondary mt-2 bg-gray-50 p-3 rounded-card border border-gray-100">
                    Pending - Payment not yet refunded
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
      <ReceiptModal />
    </div>
  );
};

export default PublicViewReturn;
